import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    Store,
    Car,
    Home,
    DollarSign,
    MapPin,
    Tag,
    Image as ImageIcon,
    X,
    AlertCircle,
    Plus,
    ChevronDown
} from 'lucide-react';
import { useCreateMarketplaceListingMutation, CreateMarketplaceListingRequest } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { API_BASE_URL } from '../../config/api';

interface LocationSuggestion {
    description: string;
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
    terms: Array<{
        offset: number;
        value: string;
    }>;
    types: string[];
}

// Common currency symbols
const currencySymbols = [
    { symbol: '₦', code: 'NGN', name: 'Nigerian Naira' },
    { symbol: '$', code: 'USD', name: 'US Dollar' },
    { symbol: '€', code: 'EUR', name: 'Euro' },
    { symbol: '£', code: 'GBP', name: 'British Pound' },
    { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
    { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
    { symbol: '₨', code: 'PKR', name: 'Pakistani Rupee' },
    { symbol: 'R$', code: 'BRL', name: 'Brazilian Real' },
    { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
    { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
    { symbol: 'CHF', code: 'CHF', name: 'Swiss Franc' },
    { symbol: '¥', code: 'CNY', name: 'Chinese Yuan' },
    { symbol: '₩', code: 'KRW', name: 'South Korean Won' },
    { symbol: '₽', code: 'RUB', name: 'Russian Ruble' },
    { symbol: 'R', code: 'ZAR', name: 'South African Rand' },
    { symbol: '₪', code: 'ILS', name: 'Israeli Shekel' },
    { symbol: '₴', code: 'UAH', name: 'Ukrainian Hryvnia' },
    { symbol: '₫', code: 'VND', name: 'Vietnamese Dong' },
    { symbol: '₱', code: 'PHP', name: 'Philippine Peso' },
    { symbol: '₵', code: 'GHS', name: 'Ghanaian Cedi' },
];

const CreateMarketplaceListing: React.FC = () => {
    const navigate = useNavigate();
    const [createListing, { isLoading: isSubmitting }] = useCreateMarketplaceListingMutation();

    const [formData, setFormData] = useState<CreateMarketplaceListingRequest>({
        listing_type: 'car',
        title: '',
        description: '',
        price: 0,
        currency_code: 'NGN',
        currency_symbol: '₦',
        location: '',
        tags: [],
        car_details: {},
        house_details: {},
        status: 'draft',
        is_featured: false,
        images: []
    });

    const [newTag, setNewTag] = useState('');
    const [errors, setErrors] = useState<Partial<Record<keyof CreateMarketplaceListingRequest, string>>>({});
    
    // Location search states
    const [locationSearch, setLocationSearch] = useState('');
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const locationRef = useRef<HTMLDivElement>(null);
    
    // Currency symbol dropdown states
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
    const currencyRef = useRef<HTMLDivElement>(null);

    const handleInputChange = (field: keyof CreateMarketplaceListingRequest, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const fileArray = Array.from(files) as File[];
            setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), ...fileArray]
            }));
        }
    };

    const handleRemoveImage = (index: number) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images?.filter((_, i) => i !== index) || []
        }));
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...(prev.tags || []), newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
        }));
    };

    const handleCarDetailChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            car_details: {
                ...prev.car_details,
                [field]: value
            }
        }));
    };

    const handleHouseDetailChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            house_details: {
                ...prev.house_details,
                [field]: value
            }
        }));
    };

    // Location search function
    const searchLocation = async (query: string) => {
        if (!query.trim()) {
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
            return;
        }

        const token = localStorage.getItem('diasporawise_admin_token');
        if (!token) {
            toast.error('You are not authenticated. Please login again.');
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
            return;
        }

        if (token.length < 10 || !token.includes('.')) {
            toast.error('Invalid authentication token. Please login again.');
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
            return;
        }

        setIsSearchingLocation(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/search-location?input=${encodeURIComponent(query)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success && data.data) {
                setLocationSuggestions(data.data);
                setShowLocationSuggestions(true);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        } catch (error: any) {
            console.error('Location search error:', error);
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
        } finally {
            setIsSearchingLocation(false);
        }
    };

    // Handle location input change with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (locationSearch) {
                searchLocation(locationSearch);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [locationSearch]);

    // Handle location selection
    const selectLocation = (location: LocationSuggestion) => {
        setLocationSearch(location.description);
        handleInputChange('location', location.description);
        setShowLocationSuggestions(false);
    };

    // Initialize locationSearch from formData.location
    useEffect(() => {
        if (formData.location && !locationSearch) {
            setLocationSearch(formData.location);
        }
    }, []);

    // Close location suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
                setShowLocationSuggestions(false);
            }
            if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
                setShowCurrencyDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle currency symbol selection
    const selectCurrencySymbol = (currency: typeof currencySymbols[0]) => {
        handleInputChange('currency_symbol', currency.symbol);
        handleInputChange('currency_code', currency.code);
        setShowCurrencyDropdown(false);
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof CreateMarketplaceListingRequest, string>> = {};

        // listing_type: required, must be "car" or "house"
        if (!formData.listing_type || (formData.listing_type !== 'car' && formData.listing_type !== 'house')) {
            newErrors.listing_type = 'Listing type is required and must be either "car" or "house"';
        }

        // title: required
        if (!formData.title || !formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        // description: required
        if (!formData.description || !formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        // price: required, must be positive number
        if (!formData.price || formData.price <= 0) {
            newErrors.price = 'Price is required and must be greater than 0';
        }

        // currency_code: optional, but if provided must be 3 characters
        if (formData.currency_code && formData.currency_code.length !== 3) {
            newErrors.currency_code = 'Currency code must be exactly 3 characters';
        }

        // currency_symbol: optional, but if provided max 5 characters
        if (formData.currency_symbol && formData.currency_symbol.length > 5) {
            newErrors.currency_symbol = 'Currency symbol must be maximum 5 characters';
        }

        // status: optional, but if provided must be one of valid values
        if (formData.status && !['draft', 'active', 'inactive', 'sold'].includes(formData.status)) {
            newErrors.status = 'Status must be one of: draft, active, inactive, sold';
        }

        // tags: optional, but if provided must be array of strings
        if (formData.tags && formData.tags.length > 0) {
            const invalidTags = formData.tags.filter(tag => typeof tag !== 'string' || !tag.trim());
            if (invalidTags.length > 0) {
                newErrors.tags = 'All tags must be non-empty strings';
            }
        }

        // car_details: required when listing_type is "car"
        if (formData.listing_type === 'car') {
            if (!formData.car_details || Object.keys(formData.car_details).length === 0) {
                newErrors.car_details = 'Car details are required for car listings';
            } else {
                // VIN: required, exactly 17 characters, pattern /^[A-HJ-NPR-Z0-9]{17}$/
                if (!formData.car_details.vin || !formData.car_details.vin.trim()) {
                    newErrors.car_details = 'VIN number is required for car listings';
                } else {
                    const vin = formData.car_details.vin.trim();
                    if (vin.length !== 17) {
                        newErrors.car_details = 'VIN number must be exactly 17 characters';
                    } else if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
                        newErrors.car_details = 'VIN number must contain only alphanumeric characters (excluding I, O, Q)';
                    }
                }

                // make: required
                if (!formData.car_details.make || !formData.car_details.make.trim()) {
                    newErrors.car_details = 'Car make is required';
                }

                // model: required
                if (!formData.car_details.model || !formData.car_details.model.trim()) {
                    newErrors.car_details = 'Car model is required';
                }

                // year: required, must be a number
                if (!formData.car_details.year || formData.car_details.year === '') {
                    newErrors.car_details = 'Year is required';
                } else {
                    const yearStr = String(formData.car_details.year).trim();
                    if (yearStr === '') {
                        newErrors.car_details = 'Year is required';
                    } else {
                        const yearNum = Number(yearStr);
                        if (isNaN(yearNum)) {
                            newErrors.car_details = 'Year must be a valid number';
                        }
                    }
                }

                // mileage: required, must be a number
                if (!formData.car_details.mileage || formData.car_details.mileage === '') {
                    newErrors.car_details = 'Mileage is required';
                } else {
                    const mileageStr = String(formData.car_details.mileage).trim();
                    if (mileageStr === '') {
                        newErrors.car_details = 'Mileage is required';
                    } else {
                        const mileageNum = Number(mileageStr);
                        if (isNaN(mileageNum)) {
                            newErrors.car_details = 'Mileage must be a valid number';
                        }
                    }
                }
            }

            // Images: minimum 2 images required for car listings
            if (!formData.images || formData.images.length < 2) {
                newErrors.images = 'At least 2 images are required for car listings';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            // Prepare car_details with proper number conversions
            let processedCarDetails = formData.car_details;
            if (formData.listing_type === 'car' && formData.car_details) {
                processedCarDetails = { ...formData.car_details };
                // Convert year to number if it exists and is not empty
                if (processedCarDetails.year !== undefined && processedCarDetails.year !== null && processedCarDetails.year !== '') {
                    const yearStr = String(processedCarDetails.year).trim();
                    if (yearStr !== '') {
                        const yearNum = Number(yearStr);
                        processedCarDetails.year = isNaN(yearNum) ? undefined : yearNum;
                    } else {
                        delete processedCarDetails.year;
                    }
                } else {
                    delete processedCarDetails.year;
                }
                // Convert mileage to number if it exists and is not empty
                if (processedCarDetails.mileage !== undefined && processedCarDetails.mileage !== null && processedCarDetails.mileage !== '') {
                    const mileageStr = String(processedCarDetails.mileage).trim();
                    if (mileageStr !== '') {
                        const mileageNum = Number(mileageStr);
                        processedCarDetails.mileage = isNaN(mileageNum) ? undefined : mileageNum;
                    } else {
                        delete processedCarDetails.mileage;
                    }
                } else {
                    delete processedCarDetails.mileage;
                }
            }

            const payload: CreateMarketplaceListingRequest = {
                ...formData,
                currency_code: formData.currency_code || 'NGN',
                images: formData.images?.filter(img => img instanceof File) as File[],
                tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
                // Always send car_details as an object when listing type is 'car'
                car_details: formData.listing_type === 'car' ? (processedCarDetails || {}) : undefined,
                // Always send house_details as an object when listing type is 'house'
                house_details: formData.listing_type === 'house' ? (formData.house_details || {}) : undefined,
            };

            const result = await createListing(payload).unwrap();
            // Only show success if the API response indicates success
            if (result?.success !== false) {
                toast.success('Marketplace listing created successfully!');
                navigate('/admin/marketplace-listings');
            } else {
                toast.error(result?.message || 'Failed to create listing');
            }
        } catch (error: any) {
            console.error('Failed to create listing:', error);
            // Extract error message from API response
            // RTK Query errors have the structure: error.data.message
            const errorMessage = error?.data?.message || error?.message || 'Failed to create listing';
            toast.error(errorMessage);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/marketplace-listings')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Create Marketplace Listing</h1>
                                <p className="text-gray-600">Add a new car or house listing</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Store className="w-5 h-5 mr-2" />
                                    Basic Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                {/* Listing Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Listing Type *
                                    </label>
                                    <div className="grid grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('listing_type', 'car')}
                                        className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-3 transition-all ${
                                            formData.listing_type === 'car'
                                                ? 'border-blue-500 bg-white'
                                                : errors.listing_type
                                                ? 'border-red-500 bg-white'
                                                : 'border-gray-200 hover:border-gray-300 bg-white'
                                        }`}
                                    >
                                        <Car className={`w-6 h-6 ${formData.listing_type === 'car' ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <span className={`font-medium ${formData.listing_type === 'car' ? 'text-blue-600' : 'text-gray-600'}`}>Car</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('listing_type', 'house')}
                                        className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-3 transition-all ${
                                            formData.listing_type === 'house'
                                                ? 'border-blue-500 bg-white'
                                                : errors.listing_type
                                                ? 'border-red-500 bg-white'
                                                : 'border-gray-200 hover:border-gray-300 bg-white'
                                        }`}
                                    >
                                        <Home className={`w-6 h-6 ${formData.listing_type === 'house' ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <span className={`font-medium ${formData.listing_type === 'house' ? 'text-blue-600' : 'text-gray-600'}`}>House</span>
                                    </button>
                                    </div>
                                    {errors.listing_type && (
                                        <p className="mt-1 text-sm text-red-600">{errors.listing_type}</p>
                                    )}
                                </div>

                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.title ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="e.g., Toyota Camry 2020"
                                    />
                                    {errors.title && (
                                        <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description *
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        rows={4}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.description ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Describe the listing..."
                                    />
                                    {errors.description && (
                                        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                                    )}
                                </div>

                                {/* Price and Currency */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Price *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center">
                                                <span className="text-gray-700 font-semibold text-lg mr-1">
                                                    {formData.currency_symbol || '₦'}
                                                </span>
                                            </div>
                                            <input
                                                type="number"
                                                value={formData.price}
                                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                                className={`w-full pl-16 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.price ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                        {errors.price && (
                                            <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                                        )}
                                    </div>
                                    <div ref={currencyRef} className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Currency Symbol
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:bg-gray-50 ${
                                                errors.currency_symbol ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        >
                                            <span className="text-gray-900">
                                                {formData.currency_symbol || '₦'}
                                            </span>
                                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showCurrencyDropdown ? 'transform rotate-180' : ''}`} />
                                        </button>
                                        
                                        {/* Currency Dropdown */}
                                        {showCurrencyDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                <div className="bg-white">
                                                    {currencySymbols.map((currency, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            onClick={() => selectCurrencySymbol(currency)}
                                                            className="w-full text-left px-4 py-3 hover:bg-white focus:bg-white active:bg-white transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                            onFocus={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                        >
                                                            <div className="flex items-center justify-between bg-white">
                                                                <div className="bg-white">
                                                                    <div className="font-medium text-gray-900">
                                                                        {currency.symbol} - {currency.name}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                                        {currency.code}
                                                                    </div>
                                                                </div>
                                                                {formData.currency_symbol === currency.symbol && (
                                                                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {errors.currency_symbol ? (
                                            <p className="mt-1 text-sm text-red-600">{errors.currency_symbol}</p>
                                        ) : (
                                            <p className="mt-1 text-xs text-gray-500">Symbol will appear next to price</p>
                                        )}
                                    </div>
                                </div>

                                {/* Location */}
                                <div ref={locationRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Location <span className="text-gray-500 text-xs">(Search cities worldwide)</span>
                                    </label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                                        <input
                                            type="text"
                                            value={locationSearch}
                                            onChange={(e) => {
                                                setLocationSearch(e.target.value);
                                                handleInputChange('location', e.target.value);
                                            }}
                                            onFocus={() => {
                                                if (locationSuggestions.length > 0) {
                                                    setShowLocationSuggestions(true);
                                                }
                                            }}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Search for a city worldwide (e.g., Lagos, London, New York)"
                                        />
                                        {isSearchingLocation && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            </div>
                                        )}
                                        
                                        {/* Location Suggestions Dropdown */}
                                        {showLocationSuggestions && locationSuggestions.length > 0 && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                <div className="bg-white">
                                                    {locationSuggestions.map((location, index) => (
                                                        <button
                                                            key={location.place_id || index}
                                                            type="button"
                                                            onClick={() => selectLocation(location)}
                                                            className="w-full text-left px-4 py-3 hover:bg-white focus:bg-white active:bg-white transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                            onFocus={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                        >
                                                            <div className="flex items-start bg-white">
                                                                <MapPin className="w-4 h-4 text-gray-400 mt-1 mr-2 flex-shrink-0" />
                                                                <div className="flex-1 bg-white">
                                                                    <div className="font-medium text-gray-900">
                                                                        {location.structured_formatting?.main_text || location.description}
                                                                    </div>
                                                                    {location.structured_formatting?.secondary_text && (
                                                                        <div className="text-sm text-gray-500 mt-0.5">
                                                                            {location.structured_formatting.secondary_text}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {formData.location && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Selected: {formData.location}
                                        </p>
                                    )}
                                </div>

                                {/* Tags */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tags
                                    </label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {formData.tags?.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    className="ml-2 text-blue-600 hover:text-blue-800"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newTag}
                                            onChange={(e) => setNewTag(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                            className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.tags ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Add a tag and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </button>
                                    </div>
                                    {errors.tags && (
                                        <p className="mt-1 text-sm text-red-600">{errors.tags}</p>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Status
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => handleInputChange('status', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.status ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="sold">Sold</option>
                                        </select>
                                        {errors.status && (
                                            <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center pt-6">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_featured}
                                                onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm font-medium text-gray-700">Featured Listing</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Car Details */}
                        {formData.listing_type === 'car' && (
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Car className="w-5 h-5 mr-2" />
                                        Car Details
                                    </h2>
                                </div>
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">VIN Number *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.vin || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                handleCarDetailChange('vin', value);
                                            }}
                                            maxLength={17}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.car_details ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 1HGBH41JXMN109186"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">17 characters, alphanumeric (excluding I, O, Q)</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.make || ''}
                                            onChange={(e) => handleCarDetailChange('make', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.car_details ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Toyota"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.model || ''}
                                            onChange={(e) => handleCarDetailChange('model', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.car_details ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Camry"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.year || ''}
                                            onChange={(e) => handleCarDetailChange('year', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.car_details ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 2020"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Mileage *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.mileage || ''}
                                            onChange={(e) => handleCarDetailChange('mileage', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.car_details ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 50000"
                                        />
                                    </div>
                                </div>
                                {errors.car_details && (
                                    <div className="px-6 pb-4">
                                        <p className="text-sm text-red-600">{errors.car_details}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* House Details */}
                        {formData.listing_type === 'house' && (
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Home className="w-5 h-5 mr-2" />
                                        House Details
                                    </h2>
                                </div>
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms *</label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.bedrooms || ''}
                                            onChange={(e) => handleHouseDetailChange('bedrooms', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.house_details ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 3"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.bathrooms || ''}
                                            onChange={(e) => handleHouseDetailChange('bathrooms', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., 2"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Square Feet</label>
                                        <input
                                            type="text"
                                            value={formData.house_details?.square_feet || ''}
                                            onChange={(e) => handleHouseDetailChange('square_feet', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., 2000"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
                                        <input
                                            type="text"
                                            value={formData.house_details?.property_type || ''}
                                            onChange={(e) => handleHouseDetailChange('property_type', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., Apartment, House"
                                        />
                                    </div>
                                </div>
                                {errors.house_details && (
                                    <div className="px-6 pb-4">
                                        <p className="text-sm text-red-600">{errors.house_details}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Images */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Images {formData.listing_type === 'car' && <span className="text-red-500 ml-1">*</span>}
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    {formData.images?.map((image, index) => (
                                        <div key={index} className="relative group">
                                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                                                {image instanceof File ? (
                                                    <img
                                                        src={URL.createObjectURL(image)}
                                                        alt={`Preview ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <ImageIcon className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    {(!formData.images || formData.images.length < 10) && (
                                        <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors">
                                            <input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                            <div className="text-center">
                                                <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                <span className="text-sm text-gray-600">Add Image</span>
                                            </div>
                                        </label>
                                    )}
                                </div>
                                <p className="text-sm text-gray-500">
                                    {formData.listing_type === 'car' 
                                        ? 'Upload at least 2 images (maximum 10). First image will be the primary image.'
                                        : 'Upload up to 10 images. First image will be the primary image.'}
                                </p>
                                {errors.images && (
                                    <p className="mt-2 text-sm text-red-600">{errors.images}</p>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-4 pb-8">
                            <Button
                                type="button"
                                onClick={() => navigate('/admin/marketplace-listings')}
                                variant="outline"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                            >
                                <Save className="w-4 h-4 mr-2" />
                                Create Listing
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateMarketplaceListing;

